import { AxiosError } from 'axios';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { getMatchFlows } from '../../api/matchFlowApi';
import { getMatchCandidates } from '../../api/matchApi';
import { ActiveFilters } from '../useTablePageParams';
import PaginationResponseDto from '../../interfaces/paginationResponse.interface';
import { FilterOptions } from '../../interfaces/filterOptions.interface';
import { FILTER_QUERY } from '../../pages/PositionDetailsPage/CandidateMatchesTable/query';
import { MATCH_CANDIDATES_STATUS } from '../../consts/enums';
// 15 sec
const REFETCH_INTERVAL = 15000;

type UseGetMatchCandidatesResponse = {
  isLoading: boolean,
  totalCount: number,
  page: number,
  limit: number,
  isError: boolean,
  data: MatchCandidateResponse[],
  error: AxiosError | null,
  refetch: () => void,
  setEnableQuery: (value: boolean) => void,
}

export const useGetMatchCandidates = (
  positionId:string,
  accountId:string,
  page: number,
  activeSort: Sort,
  activeFilters: ActiveFilters,
):UseGetMatchCandidatesResponse => {
  const [enableQuery, setEnableQuery] = useState(true);
  const {
    isLoading,
    isError,
    data,
    error,
    refetch,
  } = useQuery<MatchCandidateResults, AxiosError | null>(
    ['match-candidates', positionId, accountId, page, activeSort, activeFilters],
    () => {
      const query = {
        order: activeSort,
        contains: {
          [FILTER_QUERY.LINKEDIN_URL]: activeFilters[FILTER_QUERY.LINKEDIN_URL]?.[0],
        },
        in: {
          [FILTER_QUERY.RUN_STATUS]: [
            MATCH_CANDIDATES_STATUS.PENDING_REVIEW,
            MATCH_CANDIDATES_STATUS.PENDING_PROMOTION,
            MATCH_CANDIDATES_STATUS.PROMOTION_IN_PROGRESS,
            MATCH_CANDIDATES_STATUS.PROMOTION_FAILED,
          ],
          [FILTER_QUERY.RUN_MATCH_FLOW_ID]: activeFilters[FILTER_QUERY.RUN_ID],
        },
        where: { isArchived: false },
        page,
      };

      return getMatchCandidates(positionId, accountId, query);
    }, {
      keepPreviousData: true,
      refetchInterval: REFETCH_INTERVAL,
      enabled: enableQuery,
    });

  return {
    isLoading,
    isError,
    data: data?.results || [],
    totalCount: data?.totalCount || 0,
    limit: data?.limit || 0,
    page: data?.page || 0,
    error,
    refetch,
    setEnableQuery,
  };
};

type UseGetRunIdsResponse = {
  isError: boolean,
  isLoading: boolean,
  filterOptions: FilterOptions[],
}

export const useGetRunIds = (
  accountId: string,
  positionId: string,
  queryStr: string,
):UseGetRunIdsResponse => {
  const query = {
    select: [FILTER_QUERY.RUN_ID],
    contains: {
      [FILTER_QUERY.RUN_ID]: queryStr,
    },
  };

  const {
    isLoading,
    isError,
    data,
  } = useQuery(['match-candidates-run-ids', queryStr], () => {
    return getMatchFlows(positionId, accountId, query);
  });

  const renderOptions = data?.results?.flatMap((item: MatchFlowResponse)  => item.matchEngine?.runId ? [{
    label: item.matchEngine?.runId,
    value: item.matchEngine?.runId,
    key: item._id,
  }] : []) || [];

  return {
    filterOptions: renderOptions,
    isLoading,
    isError,
  };
};

type UseGetMatchFlows = {
  data: MatchFlowResponse[],
  totalCount: number,
  page: number,
  limit: number,
  isLoading: boolean,
  error: AxiosError | null,
  isError: boolean,
  refetch: () => void,
}

const MATCH_FLOWS_PAGE_LIMIT = 3;

export const useGetMatchFlows = (
  positionId:string,
  accountId:string,
  pageNumber = 1,
):UseGetMatchFlows => {
  const {
    data,
    isLoading,
    error,
    isError,
    refetch,
  } = useQuery<PaginationResponseDto<MatchFlowResponse>, AxiosError | null>(
    ['match-flows', positionId, accountId, pageNumber],
    () => {
      return getMatchFlows(positionId, accountId, {
        limit: MATCH_FLOWS_PAGE_LIMIT,
        page: pageNumber,
        order: { createdAt: '-' },
      });
    });

  return {
    data: data?.results || [],
    totalCount: data?.totalCount || 0,
    page: data?.page || 0,
    limit: data?.limit || 0,
    isLoading,
    error,
    isError,
    refetch,
  };
};
